import React, { FC, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useUser } from 'store/User/Context.tsx';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import { AllModuleTypes } from 'utils/types/AllModules';
import { v4 } from 'uuid';

import Indicator from 'components/atom/Indicator';
import { activeNotification } from 'components/functions/activeNotification';

import Icon, { IconEnum } from './Icon';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 85% 1fr;
  padding: 15px 0 0 0;
  align-items: center;
  margin-bottom: -10px;

  @media (min-width: 1100px) {
    grid-column: span 2;
  }

  &.openable {
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 15px 15px 0 15px;
    margin-top: 15px;
  }
`;

interface LeftSideWrapperProps {
    expanded: boolean;
  }

const LeftSideWrapper = styled.div<LeftSideWrapperProps>`
      display: flex;
      flex-direction: column;
      gap: 5px;
      overflow-y: auto;
      padding-right: 10px;
      max-height: ${(props) => props.expanded ? '300px' : 'auto'};
      transition: max-height 0.3s ease;
      -webkit-overflow-scrolling: touch;
      
      @media (max-width: 768px) {
          max-height: ${(props) => props.expanded ? '200px' : 'auto'};
      }
  `;

const NoteItem = styled.p`
    --font-weight: 400;
    line-height: 21px;
    text-align: justify;
    margin-bottom: 15px;
`;

const ExpandButtonWrapper = styled.div`
    display: flex;
    width: 100%;
`;

const ExpandButton = styled.div`
    background: none;
    padding: 5px;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 400;

    &:hover {
        outline: none;
        background: none;
        border: none;
        color: var(--color-primary);
    }

`;

interface NotesContainerProps {
    visible: boolean;
}

const NotesContainer = styled.div<NotesContainerProps>`
    display: ${(props) => props.visible ? 'block' : 'none'};
`;

export interface Note {
    content: string;
    date: string;
    userName?: string;
}

interface NoteBoxProperties {
    newestNote: Note;
    openOfferTab: (tab: string) => void;
    showNote?: () => void;
    openable?: boolean;
    numberOfNotes?: number;
    getAllNotes?: (id: string, module: AllModuleTypes, getNotes?: boolean) => Promise<Note[] | void>;
    offerId?: string | number;
    offerModule?: AllModuleTypes;
}

const NoteBox: FC<NoteBoxProperties> = ({ newestNote, openOfferTab, openable, numberOfNotes, getAllNotes, offerId, offerModule }) => {
    const { user } = useUser();
    const [showAllNotes, setShowAllNotes] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [notes, setNotes] = useState<Note[]>([]);

    const handleExpandNotes = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        if (numberOfNotes && numberOfNotes > 1) {
            if (!showAllNotes && notes.length === 0 && getAllNotes && offerId && offerModule) {
                setIsLoading(true);

                getAllNotes(offerId.toString(), offerModule, true)
                    .then((fetchedNotes) => {
                        if (fetchedNotes) {
                            setNotes(fetchedNotes as Note[]);
                        }
                    })
                    .catch((error) => {
                        const errorId = v4();
                        userApiRequests.logException(error, errorId, {
                            functionName: 'handleExpandNotes',
                            action: 'getAllNotes',
                            user: user.profile
                        });
                        activeNotification('Pobieranie notatek', 'Błąd podczas ładowania notatek, spróbuj ponownie za jakiś czas', 'danger');
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }

            setShowAllNotes(!showAllNotes);
        }
    };

    // z api dostajemy zawsze obiekt z date, content i userName - jeśli notatki nie ma w wartościach zwarcane jest null
    // po aktualizacji api dodać warunek: && newestNote.userName !== null
    const isNoteValid = newestNote && newestNote.date !== null && newestNote.content !== null;

    return (
        <>
            <Wrapper className={clsx({ openable: openable })} onClick={(event: React.MouseEvent | React.TouchEvent) => {
                event.stopPropagation();
                openOfferTab('note');
            }}>
                <LeftSideWrapper expanded={showAllNotes}>
                    {isNoteValid && <NoteItem><span>{dayjs(newestNote.date).format('DD.MM.YYYY, HH:mm')}</span> {newestNote.userName ? ` • ${newestNote.userName}` : ''} • {newestNote.content}</NoteItem>}
                    {numberOfNotes && numberOfNotes > 1 &&
                        <NotesContainer visible={showAllNotes}>
                            {isLoading
                                ? <Indicator/>
                                : notes && notes.map((note, index) => <NoteItem key={index}>
                                    <span>{dayjs(note.date).format('DD.MM.YYYY, HH:mm')}</span>
                                    {note.userName ? ` • ${note.userName}` : ''} • {note.content}
                                </NoteItem>)
                            }
                        </NotesContainer>
                    }
                </LeftSideWrapper>
            </Wrapper>
            {numberOfNotes && numberOfNotes > 1 &&
                <ExpandButtonWrapper>
                    <ExpandButton onClick={handleExpandNotes}>
                        {showAllNotes ? 'Zwiń' : `Pokaż wszystkie notatki(${numberOfNotes})`}
                        <Icon icon={showAllNotes ? IconEnum.ACCORDION_UP : IconEnum.ACCORDION_DOWN} />
                    </ExpandButton>
                </ExpandButtonWrapper>}
        </>
    );
};

export default NoteBox;
